import orderBy from 'lodash/orderBy';
import { useEffect, useMemo } from 'react';

import { useLazyOrganizationReportsQuery } from '@/api/reports/reportsApi';

import { Report } from '../types/ReportModel';
import { ReportsFilter } from '../types/ReportsFilter';
import { filterByReportSource } from '../utils/filterByReportSource';

interface Reports {
  reports: Report[];
  reportsIsPending?: boolean;
  reportsIsError?: boolean;
}

const defaultData: Report[] = [];

export const useReports = (filter: ReportsFilter): Reports => {
  const [trigger, { data, isSuccess, isError, isFetching }] =
    useLazyOrganizationReportsQuery();

  useEffect(() => {
    const request = trigger({
      query: filter.query,
      startDate: filter.startDate,
      endDate: filter.endDate,
    });

    return () => {
      request.abort();
    };
  }, [filter.query, filter.startDate, filter.endDate, trigger]);

  return useMemo<Reports>(() => {
    if (isError) {
      return {
        reports: defaultData,
        reportsIsError: true,
      };
    }

    if (isFetching) {
      return {
        reports: defaultData,
        reportsIsPending: true,
      };
    }

    if (!isSuccess) {
      return {
        reports: defaultData,
        reportsIsPending: true,
      };
    }

    const filteredReports = filterByReportSource(data, filter.reportSource);

    const reports = orderBy(filteredReports, (report) => report.reportDateISO, [
      'desc',
    ]);

    return { reports };
  }, [data, isSuccess, isError, isFetching, filter.reportSource]);
};
